import React, { useMemo, useState } from 'react'
import { PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Input, LoadingSpinner, SortableTableHeader } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { estoqueService } from '../../services/estoque'
import { useAuthContext } from '../../contexts/AuthContext'
import { useUserLogs } from '../../hooks/useUserLogs'
import type { EstoqueFornecedor } from '../../types/estoque'
import EstoqueModal, { EstoqueModalActions } from './EstoqueModal'
import EstoquePagination from './EstoquePagination'
import {
  ICON_BTN_CLASS,
  ICON_BTN_DANGER_CLASS,
  MODULE_NAME,
  PAGE_SIZE,
  PERM_LOADING,
  SELECT_CLASS,
  TEXTAREA_CLASS,
  paginateRows,
  sortRows,
  statusBadgeClass,
} from './estoqueUi'

type Props = {
  fornecedores: EstoqueFornecedor[]
  loading: boolean
  onChanged: () => void
  canInsert: boolean
  canUpdate: boolean
  canDelete: boolean
  /** Módulo usado nas PermissionGuard dos botões (padrão: marketing_estoque). */
  moduleName?: string
}

const BR_UFS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
  'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC',
  'SP', 'SE', 'TO',
]

const emptyForm = {
  name: '',
  address: '',
  city: '',
  state: '',
  contact_name: '',
  phone: '',
  email: '',
  notes: '',
  is_active: true,
}

const EstoqueFornecedoresTab: React.FC<Props> = ({
  fornecedores,
  loading,
  onChanged,
  canInsert,
  canUpdate,
  canDelete,
  moduleName = MODULE_NAME,
}) => {
  const { user } = useAuthContext()
  const { logCreate, logUpdate, logDelete } = useUserLogs()
  const [sortKey, setSortKey] = useState('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(1)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<EstoqueFornecedor | null>(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const sorted = useMemo(
    () =>
      sortRows(fornecedores, sortKey, sortOrder, (row, key) => {
        if (key === 'name') return row.name
        if (key === 'city') return row.city
        if (key === 'state') return row.state
        if (key === 'contact') return row.contact_name
        if (key === 'active') return row.is_active ? 1 : 0
        return ''
      }),
    [fornecedores, sortKey, sortOrder]
  )
  const pageRows = paginateRows(sorted, page)
  const toggleSort = (key: string) => {
    if (sortKey === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      setSortOrder('asc')
    }
    setPage(1)
  }
  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm)
    setError(null)
    setModalOpen(true)
  }
  const openEdit = (row: EstoqueFornecedor) => {
    setEditing(row)
    setForm({
      name: row.name,
      address: row.address || '',
      city: row.city || '',
      state: row.state || '',
      contact_name: row.contact_name || '',
      phone: row.phone || '',
      email: row.email || '',
      notes: row.notes || '',
      is_active: row.is_active,
    })
    setError(null)
    setModalOpen(true)
  }
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      setError('Nome é obrigatório.')
      return
    }
    if (form.state && !BR_UFS.includes(form.state)) {
      setError('Selecione uma UF válida.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      if (editing) {
        const updated = await estoqueService.updateFornecedor(editing.id, form)
        void logUpdate(
          'supplier',
          updated.id,
          `Atualizou fornecedor de estoque: ${updated.name}`,
          {
            name: editing.name,
            address: editing.address,
            city: editing.city,
            state: editing.state,
            contact_name: editing.contact_name,
            phone: editing.phone,
            email: editing.email,
            notes: editing.notes,
            is_active: editing.is_active,
          },
          {
            name: updated.name,
            address: updated.address,
            city: updated.city,
            state: updated.state,
            contact_name: updated.contact_name,
            phone: updated.phone,
            email: updated.email,
            notes: updated.notes,
            is_active: updated.is_active,
          }
        )
      } else {
        const created = await estoqueService.createFornecedor(form)
        void logCreate(
          'supplier',
          created.id,
          `Criou fornecedor de estoque: ${created.name}`,
          {
            name: created.name,
            address: created.address,
            city: created.city,
            state: created.state,
            contact_name: created.contact_name,
            is_active: created.is_active,
          }
        )
      }
      setModalOpen(false)
      onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar fornecedor.')
    } finally {
      setSaving(false)
    }
  }
  const handleDelete = async (row: EstoqueFornecedor) => {
    if (!window.confirm(`Excluir o fornecedor "${row.name}"?`)) return
    try {
      await estoqueService.softDeleteFornecedor(row.id, {
        deleted_by: user?.id,
        deleted_reason: 'Exclusão pela tela de estoque',
      })
      void logDelete('supplier', row.id, `Excluiu fornecedor de estoque: ${row.name}`, {
        name: row.name,
        address: row.address,
        city: row.city,
        state: row.state,
      })
      onChanged()
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Erro ao excluir fornecedor.')
    }
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <Heading as="h2" size="xl" className="text-gray-900 dark:text-white">
            Fornecedores
          </Heading>
        </div>
        {canInsert && (
          <PermissionGuard moduleName={moduleName} permissionType="insert" loadingComponent={PERM_LOADING} fallback={null}>
            <Button variant="solid" onClick={openCreate} className="flex items-center gap-1">
              <PlusIcon className="h-5 w-5" />
              Novo fornecedor
            </Button>
          </PermissionGuard>
        )}
      </div>
      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/30">
                <tr>
                  <SortableTableHeader sortKey="name" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3">
                    Nome
                  </SortableTableHeader>
                  <SortableTableHeader sortKey="city" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3">
                    Cidade
                  </SortableTableHeader>
                  <SortableTableHeader sortKey="state" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3">
                    UF
                  </SortableTableHeader>
                  <SortableTableHeader sortKey="contact" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3">
                    Contato
                  </SortableTableHeader>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Telefone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">E-mail</th>
                  <SortableTableHeader sortKey="active" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3">
                    Status
                  </SortableTableHeader>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      Nenhum fornecedor cadastrado.
                    </td>
                  </tr>
                ) : (
                  pageRows.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/20">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{row.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{row.city || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{row.state || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{row.contact_name || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{row.phone || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{row.email || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(row.is_active)}`}>
                          {row.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {canUpdate && (
                            <PermissionGuard moduleName={moduleName} permissionType="update" loadingComponent={PERM_LOADING} fallback={null}>
                              <button type="button" onClick={() => openEdit(row)} className={ICON_BTN_CLASS} title="Editar">
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            </PermissionGuard>
                          )}
                          {canDelete && (
                            <PermissionGuard moduleName={moduleName} permissionType="delete" loadingComponent={PERM_LOADING} fallback={null}>
                              <button type="button" onClick={() => void handleDelete(row)} className={ICON_BTN_DANGER_CLASS} title="Excluir">
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </PermissionGuard>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <EstoquePagination page={page} total={sorted.length} onPageChange={setPage} pageSize={PAGE_SIZE} />
      {modalOpen && (
        <EstoqueModal
          title={editing ? 'Editar fornecedor' : 'Novo fornecedor'}
          onClose={() => setModalOpen(false)}
          busy={saving}
          onSubmit={handleSave}
          footer={<EstoqueModalActions onCancel={() => setModalOpen(false)} saving={saving} />}
        >
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Nome <span className="text-red-500">*</span>
            </label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Endereço</label>
            <textarea
              value={form.address}
              onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              rows={2}
              placeholder="Rua, número, bairro"
              className={TEXTAREA_CLASS}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_7rem]">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Cidade</label>
              <Input value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">UF</label>
              <select
                value={form.state}
                onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
                className={`${SELECT_CLASS} w-full`}
              >
                <option value="">—</option>
                {BR_UFS.map((uf) => (
                  <option key={uf} value={uf}>
                    {uf}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Contato</label>
              <Input value={form.contact_name} onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Telefone</label>
              <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">E-mail</label>
              <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Observações</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={3}
              className={TEXTAREA_CLASS}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Ativo
          </label>
        </EstoqueModal>
      )}
    </div>
  )
}

export default EstoqueFornecedoresTab
